import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router'
import {
  MagnifyingGlassIcon,
  CaretDownIcon,
  PlusIcon,
} from '@phosphor-icons/react'
import ClientModal from '../../components/ClientModal/ClientModal'
import ClientsTable, {
  ClientRow,
  getClientStatus,
} from '../../components/ClientsTable/ClientsTable'
import { getEngagementStatus } from '../../components/EngagementsTable/EngagementsTable'
import { useGetAllClientsQuery } from '../../redux/client.endpoints'
import { useLazyGetAllEngagementsQuery } from '../../redux/engagement.endpoints'
import { useGetAllTeamsQuery } from '../../redux/team.endpoints'
import { Client } from '../../types/Client'
import { User } from '../../types/User'

interface Props {
  user: User
}

type ModalState = { mode: 'add' } | { mode: 'edit'; client: Client } | null

const ClientsView = ({ user }: Props) => {
  const navigate = useNavigate()
  const companyId = user.company.id
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('All Statuses')
  const [modal, setModal] = useState<ModalState>(null)

  const { data: clientsData = [] } = useGetAllClientsQuery(companyId.toString())
  const { data: teamsData = [] } = useGetAllTeamsQuery(companyId)
  const [getEngagements, { data: engagementsData = [] }] =
    useLazyGetAllEngagementsQuery()

  useEffect(() => {
    getEngagements({
      companyId,
      pageSize: 0,
      pageNumber: 0,
      sortBy: 'id',
      searchQuery: '',
      sortType: 'DESC',
    })
  }, [companyId, getEngagements])

  const clientRows: ClientRow[] = useMemo(() => {
    return clientsData.map(client => {
      const activeEngagements = engagementsData.filter(
        engagement =>
          engagement.client?.id === client.id &&
          getEngagementStatus(engagement) === 'Active',
      ).length
      const teamsCount = teamsData.filter(
        team => team.clientId === client.id,
      ).length

      return {
        ...client,
        activeEngagements,
        teamsCount,
      }
    })
  }, [clientsData, engagementsData, teamsData])

  const filteredClients = useMemo(() => {
    return clientRows.filter(client => {
      if (
        searchQuery &&
        !client.name.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return false
      }
      if (
        statusFilter !== 'All Statuses' &&
        getClientStatus(client) !== statusFilter
      ) {
        return false
      }
      return true
    })
  }, [clientRows, searchQuery, statusFilter])

  return (
    <div className="container my-4 management-clients">
      <Helmet title="Time Off - Clients" />

      <ClientModal
        isOpen={!!modal}
        mode={modal?.mode ?? 'add'}
        client={modal?.mode === 'edit' ? modal.client : undefined}
        companyId={companyId}
        onClose={() => setModal(null)}
      />

      <div className="management-clients__header">
        <h1 className="management-clients__title">Manage Clients</h1>
        <div className="management-clients__actions">
          <button
            type="button"
            className="management-clients__btn management-clients__btn--primary"
            onClick={() => setModal({ mode: 'add' })}
          >
            <PlusIcon size={16} weight="regular" color="#ffffff" />
            Add New Client
          </button>
        </div>
      </div>

      <div className="management-clients__filters">
        <div className="management-clients__search">
          <input
            type="text"
            className="management-clients__search-input"
            placeholder="Search by Client"
            aria-label="Search by Client"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <MagnifyingGlassIcon
            size={16}
            weight="regular"
            className="management-clients__search-icon"
          />
        </div>

        <div className="management-clients__filter-row">
          <div className="management-clients__filter">
            <span className="management-clients__filter-label">Status</span>
            <div className="management-clients__filter-select-wrap">
              <select
                className="management-clients__filter-select"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                {['All Statuses', 'Active', 'Inactive'].map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <CaretDownIcon
                size={14}
                weight="regular"
                className="management-clients__filter-caret"
              />
            </div>
          </div>
        </div>
      </div>

      <ClientsTable
        clients={filteredClients}
        onRowClick={client => navigate(`/clients/${client.id}`)}
        onEdit={client => setModal({ mode: 'edit', client })}
      />
    </div>
  )
}

export default ClientsView
